<template>
  <div v-if="error" class="vme-error-banner" style="z-index: 9999">
    <div class="vme-error-content" :class="{ 'wide-banner': showReport }">
      <div class="vme-error-body">
        <div class="d-flex justify-content-between align-items-start">
          <h3 class="error-title mb-0">Ups, da ist etwas schiefgelaufen. Bitte versuchen Sie es erneut.</h3>
          <button type="button" class="close-btn" @click="close" aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        <div v-if="showReport" class="mt-4">
          <div  class="error-help-view">
            <div class="mb-3">
              <strong class="text-color-gray">Was Sie selbst versuchen können, um den Fehler zu beheben:</strong>
              <ol>
                <li>
                  Browser Cache & Cookies löschen:
                  <table class="table table-sm table-borderless mb-0" style="font-size: 0.85rem;">
                    <tbody>
                    <tr>
                      <th scope="row" class="text-color-65-gray" style="width: 120px;">Microsoft Edge</th>
                      <td class="text-color-gray font-weight-bold"><a href='https://browser-cache-leeren.de/windows/microsoft-edge.html' target="_blank">Anleitung</a></td>
                    </tr>
                    <tr>
                      <th scope="row" class="text-color-65-gray">Google Chrome</th>
                      <td class="text-color-gray font-weight-bold"><a href='https://browser-cache-leeren.de/windows/google-chrome.html' target="_blank">Anleitung</a></td>
                    </tr>
                    <tr>
                      <th scope="row" class="text-color-65-gray">Apple Safari</th>
                      <td class="text-color-gray font-weight-bold"><a href='https://browser-cache-leeren.de/apple-mac-os/safari.html' target="_blank">Anleitung</a></td>
                    </tr>
                    <tr>
                      <th scope="row" class="text-color-65-gray">Mozilla Firefox</th>
                      <td class="text-color-gray font-weight-bold"><a href='https://browser-cache-leeren.de/windows/mozilla-firefox.html' target="_blank">Anleitung</a></td>
                    </tr>
                    </tbody>
                  </table>
                </li>
                <li>
                  Seite <a :href="pageBaseUrl" class="text-color-primary" target="_blank">{{ pageBaseUrl }}</a> neu laden
                </li>
              </ol>
            </div>
            <div class="mb-3">
              <strong class="text-color-gray">Besteht der Fehler weiterhin? Wir helfen gern. Stellen Siegern ein Ticket beim VME Support:</strong>
              <ol class="mt-2 text-color-65-gray" style="font-size: 0.9rem;">
                <li>
                  Kopieren sie die Fehlerinformationen
                  <a href="#" @click.prevent="copyToClipboard" class="text-color-primary text-decoration-underline">hier</a>.
                </li>
                <li>
                  Erstellen sie ein neues Ticket im VME-Support über das <a href="https://epr.atlassian.net/servicedesk/customer/portal/3/group/7/create/19" class="text-color-primary" target="_blank">Portal</a> oder per <a href="mailto:support@meinvme.de" class="text-color-primary">E-Mail</a>.
                </li>
              </ol>
            </div>
            <div class="table-responsive custom-table-scroll">
              <strong class="text-color-gray">Informationen, die kopiert werden: </strong>
              <table class="table table-sm table-borderless mb-0" style="font-size: 0.85rem;">
              <tbody>
                <tr>
                  <th scope="row" class="text-color-65-gray" style="width: 120px;">User ID</th>
                  <td class="text-color-gray font-weight-bold">{{ userId }}</td>
                </tr>
                <tr>
                  <th scope="row" class="text-color-65-gray">Merchant ID</th>
                  <td class="text-color-gray font-weight-bold">{{ merchantId }}</td>
                </tr>
                <tr>
                  <th scope="row" class="text-color-65-gray">Method</th>
                  <td class="text-color-gray font-weight-bold">{{ error.method || 'Unknown' }}</td>
                </tr>
                <tr>
                  <th scope="row" class="text-color-65-gray">URL</th>
                  <td class="text-break text-color-gray font-weight-bold">{{ error.url || 'Unknown' }}</td>
                </tr>
                <tr v-if="error.payload">
                  <th scope="row" class="text-color-65-gray">Payload</th>
                  <td class="text-break"><pre class="m-0 p-2 bg-gray rounded text-color-gray" style="font-size: 0.8rem; border: 1px solid #e6e6e6;">{{ formattedPayload }}</pre></td>
                </tr>
                <tr>
                  <th scope="row" class="text-color-65-gray">Details</th>
                  <td class="text-break"><pre class="m-0 p-2 bg-gray rounded text-color-gray" style="font-size: 0.8rem; border: 1px solid #e6e6e6;">{{ error.details || 'Keine weiteren Details verfügbar.' }}</pre></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

        <div class="d-flex justify-content-end align-items-center mt-3">
          <button @click.prevent="retry" class="btn btn-primary px-4">Erneut versuchen</button>
          <button @click.prevent="showReport = !showReport" class="btn btn-sm btn-outline-secondary">
            {{ showReport ? 'Ausblenden' : 'Hilfe erhalten' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia'
import { useAppStore } from '@/store'

export default {
  data() {
    return {
      showReport: false
    }
  },
  computed: {
    ...mapState(useAppStore, {
      error: 'getError'
    }),
    errorMessage() {
      if (!this.error) return ''
      return typeof this.error === 'string' ? this.error : this.error.message
    },
    formattedPayload() {
      if (!this.error || !this.error.payload) return null
      if (typeof this.error.payload === 'string') return this.error.payload
      try {
        return JSON.stringify(this.error.payload, null, 2)
      } catch (e) {
        return this.error.payload
      }
    },
    errorDetails() {
      if (!this.error || typeof this.error === 'string') return 'Keine weiteren Details verfügbar.'
      let details = ''
      details += `User ID: ${this.userId}\n`
      details += `Merchant ID: ${this.merchantId}\n`
      details += `Method: ${this.error.method || 'Unknown'}\n`
      details += `URL: ${this.error.url || 'Unknown'}\n`
      if (this.error.payload) {
        details += `Payload: ${this.formattedPayload}\n`
      }
      details += `\nDetails:\n${this.error.details || 'Keine weiteren Details verfügbar.'}`
      return details
    },
    userId() {
      return sessionStorage.getItem('userId') || 'None'
    },
    merchantId() {
      return sessionStorage.getItem('companyId') || 'None'
    },
    pageBaseUrl() {
      return window.location.origin
    }
  },
  methods: {
    ...mapActions(useAppStore, ['SET_ERROR']),
    async retry() {
      this.SET_ERROR(null)
      this.showReport = false
      window.location.reload()
    },
    close() {
      this.SET_ERROR(null)
      this.showReport = false
    },
    copyToClipboard() {
      const reportText = `Error: ${this.errorMessage}\n\n${this.errorDetails}`
      navigator.clipboard.writeText(reportText).then(() => {
        alert('Fehlerinformationen in die Zwischenablage kopiert!')
      }).catch(err => {
        console.error('Failed to copy: ', err)
      })
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/assets/scss/global/variables.scss";

.vme-error-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  padding: 1.5rem;
  pointer-events: none;
}

.vme-error-content {
  background: $vme-white;
  border: 1px solid $vme-15-anthrazit;
  border-top: 5px solid $vme-alert-red;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  width: 100%;
  max-width: 650px;
  pointer-events: auto;
  transition: max-width 0.3s ease;
}

.wide-banner {
  max-width: 1000px;
}

.vme-error-body {
  padding: 1.5rem;
}

.error-title {
  color: $vme-alert-red;
  font-weight: 600;
  font-size: 1.25rem;
}

.close-btn {
  background: transparent;
  border: none;
  font-size: 1.5rem;
  line-height: 1;
  color: $vme-65-anthrazit;
  cursor: pointer;
  padding: 0;
  margin-top: -5px;
  margin-right: -5px;
  transition: color 0.2s;

  &:hover {
    color: $vme-anthrazit;
  }
}

.error-help-view {
  border-top: 1px solid $vme-15-anthrazit;
  padding-top: 1.5rem;
}

.custom-table-scroll {
  max-height: 400px;
  overflow-y: auto;
  padding-right: 5px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: $vme-gray;
  }
  &::-webkit-scrollbar-thumb {
    background: $vme-light-gray;
    border-radius: 10px;
  }
}

.bg-gray {
  background-color: $vme-gray;
}

.text-color-primary {
  color: $vme-orange !important;
}

.text-color-gray {
  color: $vme-anthrazit !important;
}

.text-color-65-gray {
  color: $vme-65-anthrazit !important;
}
</style>
